// src/components/TradeForm.js (최종 수정된 버전)

import React, { useState, useEffect } from 'react';
import { useContract } from '../hooks/useContract';
import { getCurrentFeePercent, getCurrentFeePercentage, feePercentToPercentage, DEFAULT_FEE_PERCENT } from '../utils/contract';
import './TradeForm.css';

const TradeForm = () => {
  const [formData, setFormData] = useState({
    buyerAddress: '',
    wlcAmount: '',
    krwAmount: '',
    durationHours: '24'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentFeePercent, setCurrentFeePercent] = useState(DEFAULT_FEE_PERCENT); // 환경변수 기본값 사용
  const [currentFeePercentage, setCurrentFeePercentage] = useState(feePercentToPercentage(DEFAULT_FEE_PERCENT));

  const { createTrade, loading, error, clearError } = useContract();

  // 현재 수수료율 로드
  useEffect(() => {
    const loadCurrentFeeRate = async () => {
      try {
        const [feePercent, feePercentage] = await Promise.all([
          getCurrentFeePercent(),
          getCurrentFeePercentage()
        ]);
        setCurrentFeePercent(feePercent);
        setCurrentFeePercentage(feePercentage);
      } catch (error) {
        console.error('수수료율 로드 실패:', error);
        // 실패 시 기본값 유지
      }
    };

    loadCurrentFeeRate();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // 에러 초기화
    if (error) {
      clearError();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // 입력값 검증
      if (!formData.buyerAddress || !formData.wlcAmount || !formData.krwAmount) {
        throw new Error('모든 필드를 입력해주세요.');
      }

      if (formData.buyerAddress.length !== 42 || !formData.buyerAddress.startsWith('0x')) {
        throw new Error('올바른 지갑 주소를 입력해주세요.');
      }

      if (parseFloat(formData.wlcAmount) <= 0) {
        throw new Error('WLC 수량은 0보다 커야 합니다.');
      }

      if (parseFloat(formData.krwAmount) <= 0) {
        throw new Error('원화 금액은 0보다 커야 합니다.');
      }

      console.log('거래 생성 시작:', formData);

      // 거래 생성
      const tradeId = await createTrade(
        formData.buyerAddress,
        formData.wlcAmount,
        formData.krwAmount,
        parseInt(formData.durationHours)
      );

      console.log('거래 생성 성공! 거래 ID:', tradeId);

      alert(`거래가 성공적으로 생성되었습니다!\n거래 ID: ${tradeId}\n\n구매자에게 거래 ID를 공유하고 원화 송금을 기다려주세요.`);

      // 폼 초기화
      setFormData({
        buyerAddress: '',
        wlcAmount: '',
        krwAmount: '',
        durationHours: '6'
      });

    } catch (error) {
        console.error('거래 생성 실패:', error);
        
        let errorMessage = error.message;
        
        if (error.message.includes('User denied transaction signature')) {
            errorMessage = '트랜잭션이 취소되었습니다.';
        } else if (error.message.includes('insufficient funds')) {
            errorMessage = 'WLC 잔액 또는 가스비가 부족합니다.';
        } else if (error.message.includes('execution reverted')) {
            errorMessage = '스마트 컨트랙트 실행이 실패했습니다. 입력값을 다시 확인해주세요.';
        } else if (error.message.includes('트랜잭션 해시:')) {
            // 거래는 성공했지만 ID 감지 실패인 경우
            alert(error.message + '\n\n잠시 후 거래 목록을 확인해보세요.');
            
            // 폼 초기화는 여전히 진행
            setFormData({
            buyerAddress: '',
            wlcAmount: '',
            krwAmount: '',
            durationHours: '6'
            });
            return; // 여기서 함수 종료
        }
        
        alert(`거래 생성 실패: ${errorMessage}`);
        } finally {
        setIsSubmitting(false);
        }
  };

  return (
    <div className="trade-form-container">
      <div className="trade-form-card">
        <h3>새 거래 생성</h3>
        <p>WLC를 원화로 판매하는 거래를 생성합니다.</p>

        <form onSubmit={handleSubmit} className="trade-form">
          <div className="form-group">
            <label htmlFor="buyerAddress">구매자 지갑 주소</label>
            <input
              type="text"
              id="buyerAddress"
              name="buyerAddress"
              value={formData.buyerAddress}
              onChange={handleInputChange}
              placeholder="0x..."
              className="form-input"
              disabled={isSubmitting || loading}
            />
            <small>거래할 상대방의 지갑 주소를 입력하세요</small>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="wlcAmount">판매할 WLC 수량</label>
              <input
                type="number"
                id="wlcAmount"
                name="wlcAmount"
                value={formData.wlcAmount}
                onChange={handleInputChange}
                placeholder="예: 100"
                step="0.001"
                min="0.001"
                className="form-input"
                disabled={isSubmitting || loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="krwAmount">받을 원화 금액</label>
              <input
                type="number"
                id="krwAmount"
                name="krwAmount"
                value={formData.krwAmount}
                onChange={handleInputChange}
                placeholder="예: 120000"
                min="1"
                className="form-input"
                disabled={isSubmitting || loading}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="durationHours">거래 유효 시간 (시간)</label>
            <select
              id="durationHours"
              name="durationHours"
              value={formData.durationHours}
              onChange={handleInputChange}
              className="form-select"
              disabled={isSubmitting || loading}
            >
              <option value="1">1시간 (빠른 거래) ⚡</option>
              <option value="3">3시간 (권장) ⭐</option>
              <option value="6">6시간 (여유)</option>
              <option value="12">12시간</option>
              <option value="24">24시간</option>
            </select>
            <small>거래가 자동으로 만료되는 시간을 설정하세요</small>
          </div>

          {/* 거래 요약 */}
          {formData.wlcAmount && formData.krwAmount && (
            <div className="trade-summary">
              <h4>거래 요약</h4>
              <div className="summary-item">
                <span>판매할 WLC:</span>
                <span>{formData.wlcAmount} WLC</span>
              </div>
              <div className="summary-item">
                <span>받을 원화:</span>
                <span>{parseInt(formData.krwAmount).toLocaleString()} 원</span>
              </div>
              <div className="summary-item">
                <span>개당 가격:</span>
                <span>{Math.round(formData.krwAmount / formData.wlcAmount).toLocaleString()} 원/WLC</span>
              </div>
              <div className="summary-item fee-info">
                <span>수수료 ({currentFeePercentage}):</span>
                <span>{(formData.wlcAmount * currentFeePercent / 10000).toFixed(3)} WLC</span>
              </div>
              <div className="summary-item highlight">
                <span>구매자가 받을 WLC:</span>
                <span>{(formData.wlcAmount * (1 - currentFeePercent / 10000)).toFixed(3)} WLC</span>
              </div>
            </div>
          )}

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <button
            type="submit"
            className="submit-button"
            disabled={isSubmitting || loading}
          >
            {isSubmitting || loading ? (
              <>
                <span className="loading-spinner"></span>
                거래 생성 중...
              </>
            ) : (
              '거래 생성하기'
            )}
          </button>
        </form>

        <div className="form-info">
          <h4>주의사항</h4>
          <ul>
            <li>거래 생성 시 설정한 WLC가 즉시 스마트 컨트랙트에 락업됩니다.</li>
            <li>구매자가 원화 송금 후 판매자가 확인해야 거래가 완료됩니다.</li>
            <li>설정한 시간 내에 거래가 완료되지 않으면 자동으로 취소됩니다.</li>
            <li>거래 완료 시 현재 수수료율 <strong>{currentFeePercentage}</strong>가 적용됩니다.</li>
            <li>거래 생성 후에는 수수료율이 변경되어도 이 거래에는 영향을 주지 않습니다.</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default TradeForm;